import { Card } from "@/components/ui/card";
import { Briefcase, GraduationCap, Award } from "lucide-react";
import { useScrollReveal, useStaggeredReveal } from "@/hooks/useScrollReveal";

const experiences = [
  {
    icon: Briefcase,
    title: "CTO & AI Architect",
    company: "ScaleLogix AI Consulting",
    period: "August 2025 - Present",
    description: "Overseeing technology strategy, software architecture, AI systems implementation, and technical planning. Building production-grade AI & automations systems.",
    impact: "Full-stack AI systems"
  },
  {
    icon: Briefcase,
    title: "Co-Founder",
    company: "sAIve time",
    period: "August 2025 - Present",
    description: "Co-founded with Leo Tsandarliotis. Offering AI Automation Bundles with custom AI automations, n8n workflows, AI Agents (SDR, Chat, Voice), CRM integrations. Plus Productized Lead Generation System covering LinkedIn, cold email, and appointment booking.",
    impact: "Custom AI solutions, Lead generation systems"
  },
  {
    icon: Briefcase,
    title: "Data Science Intern",
    company: "Ontovisual",
    period: "April 2024 - May 2024",
    description: "Built prediction models for World Bank economic indicators. Worked with real messy data: missing values, weird formats, APIs. Developed stock prediction and economic indicator forecasting models.",
    impact: "Real-world ML, Production systems"
  },
  {
    icon: GraduationCap,
    title: "AI & Unity Instructor",
    company: "Algorithmics",
    period: "September 2024 - August 2025",
    description: "Taught 75+ students across AI tools and Unity Game Development. Created scalable teaching methods maintaining 93.33% success rate. Explaining complex concepts to beginners improved my own understanding.",
    impact: "75+ students, 93.33% success rate"
  },
  {
    icon: GraduationCap,
    title: "Diploma Thesis",
    company: "University of Ioannina",
    period: "2024 - 2025",
    description: "Completed Diploma Thesis on medical imaging AI research. Developed multimodal MRI super-resolution using interpretable deep neural networks. Improved on state-of-the-art by +0.83 dB.",
    impact: "+0.83 dB improvement, Thesis completed"
  },
  {
    icon: Award,
    title: "Kaggle Competition",
    company: "Citation Link Prediction",
    period: "January 2025",
    description: "Achieved top places in Citation Link Prediction competition with 99.33% AUC and 96.24% accuracy through innovative multi-modal feature integration.",
    impact: "99.33% AUC, 96.24% accuracy"
  }
];

const ExperienceCard = ({ exp, index, isVisible }: { exp: typeof experiences[0], index: number, isVisible: boolean }) => {
  return (
    <div
      className={`md:ml-20 transition-all duration-700 ${
        isVisible
          ? "opacity-100 translate-x-0"
          : "opacity-0 -translate-x-10"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <Card className="p-6 card-enhanced relative group overflow-hidden">
        {/* Accent line */}
        <div className="absolute top-0 left-0 w-1 h-full bg-primary transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500" />

        {/* Timeline dot */}
        <div className="absolute -left-[52px] top-8 hidden md:flex items-center justify-center">
          <div className="w-4 h-4 rounded-full bg-primary shadow-lg group-hover:scale-150 transition-all duration-300" />
        </div>

        <div className="relative z-10 flex gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
              <exp.icon className="w-6 h-6 text-primary" />
            </div>
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <h3 className="text-xl font-bold group-hover:text-primary transition-colors duration-300">
                {exp.title}
              </h3>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1">
                <p className="font-medium text-primary">
                  {exp.company}
                </p>
                <span className="hidden sm:inline text-muted-foreground">•</span>
                <p className="text-sm text-muted-foreground">
                  {exp.period}
                </p>
              </div>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              {exp.description}
            </p>
            <div className="pt-3 border-t border-border/30">
              <p className="text-sm font-medium text-primary">
                Impact: {exp.impact}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

const Experience = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { containerRef: timelineRef, visibleItems } = useStaggeredReveal(experiences.length, 150);

  return (
    <section id="experience" className="py-32 px-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-transparent to-secondary/30" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Floating elements */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-48 h-48 bg-primary/5 rounded-full blur-3xl" />

      <div className="container max-w-6xl mx-auto relative z-10">
        <div
          ref={headerRef}
          className={`text-center mb-20 space-y-6 transition-all duration-1000 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-violet-400 [text-shadow:0_0_30px_rgba(167,139,250,0.7),0_0_60px_rgba(139,92,246,0.4)]">
            Experience
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Track record of delivering exceptional results across research, education, and industry
          </p>
        </div>

        <div ref={timelineRef} className="relative">
          {/* Timeline line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 hidden md:block bg-gradient-to-b from-primary via-primary/50 to-primary" />

          <div className="space-y-6">
            {experiences.map((exp, index) => (
              <ExperienceCard
                key={index}
                exp={exp}
                index={index}
                isVisible={visibleItems[index]}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
